import React from 'react';
import { AlertTriangle, CreditCard, Key, Clock, Wrench, type LucideIcon } from 'lucide-react';
import { RunResult, ErrorClassification } from '../../types';

interface RunResultErrorPanelProps {
  runResult: RunResult;
  onEditAgent?: (agentId: number, focusField: 'model') => void;
  className?: string;
}

// AC-1533: classifications that route through the frontend's onEditAgent
// deep-link seam rather than opening a URL. The backend uses the
// 'edit-agent://' URL scheme as a sentinel; this set is the source of
// truth for "which classification should call onEditAgent".
const EDIT_AGENT_CLASSIFICATIONS: ReadonlySet<ErrorClassification> = new Set([
  'model_not_found',
  'context_length_exceeded',
]);

// Color and icon mapping for the friendly callout. Reused for visual
// consistency across the five provider error classifications.
function calloutStyleFor(
  classification: ErrorClassification
): {
  containerClass: string;
  iconClass: string;
  buttonClass: string;
  icon: LucideIcon;
} {
  switch (classification) {
    case 'insufficient_credits':
      return {
        containerClass:
          'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
        iconClass: 'text-orange-600 dark:text-orange-400',
        buttonClass: 'bg-orange-600 hover:bg-orange-700',
        icon: CreditCard,
      };
    case 'rate_limited':
      return {
        containerClass:
          'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
        iconClass: 'text-orange-600 dark:text-orange-400',
        buttonClass: 'bg-orange-600 hover:bg-orange-700',
        icon: Clock,
      };
    case 'invalid_api_key':
      return {
        containerClass:
          'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
        iconClass: 'text-red-600 dark:text-red-400',
        buttonClass: 'bg-red-600 hover:bg-red-700',
        icon: Key,
      };
    case 'model_not_found':
    case 'context_length_exceeded':
      return {
        containerClass:
          'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
        iconClass: 'text-blue-600 dark:text-blue-400',
        buttonClass: 'bg-blue-600 hover:bg-blue-700',
        icon: Wrench,
      };
    default:
      return {
        containerClass:
          'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
        iconClass: 'text-yellow-600 dark:text-yellow-400',
        buttonClass: 'bg-yellow-600 hover:bg-yellow-700',
        icon: AlertTriangle,
      };
  }
}

const RunResultErrorPanel: React.FC<RunResultErrorPanelProps> = ({
  runResult,
  onEditAgent,
  className = '',
}) => {
  if (!runResult.error_message) {
    return null;
  }

  const meta = runResult.error_metadata;

  // AC-1533: render the friendly callout when the backend lifted a
  // user_message onto the metadata (always present for the five provider
  // classifications). Falls back to the AC-1349 raw-error box for legacy
  // classifications without a user_message.
  const hasFriendlyCallout = !!meta && !!meta.user_message;

  // AC-1349: the underpowered-model callout fires on timeout / max_turns
  // when suggested_model is populated. Independent from the AC-1533 callout
  // -- both can render together when the metadata carries both signals.
  const showUnderpoweredCallout =
    !!meta &&
    (meta.classification === 'timeout' || meta.classification === 'max_turns') &&
    !!meta.suggested_model;

  const style = meta ? calloutStyleFor(meta.classification) : null;
  const FriendlyIcon = style?.icon;

  return (
    <div
      data-testid="run-result-error-panel"
      className={`space-y-2 ${className}`}
    >
      {hasFriendlyCallout && meta && style && FriendlyIcon && (
        <div
          role="alert"
          aria-live="polite"
          className={`p-3 rounded-lg border ${style.containerClass}`}
        >
          <div className="flex items-start space-x-2">
            <FriendlyIcon
              aria-hidden="true"
              className={`w-5 h-5 flex-shrink-0 mt-0.5 ${style.iconClass}`}
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                {meta.user_message}
              </p>
              {(meta.action_label || meta.action_url) && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {EDIT_AGENT_CLASSIFICATIONS.has(meta.classification) ? (
                    <button
                      type="button"
                      onClick={() => onEditAgent?.(meta.agent_id, 'model')}
                      className={`px-3 py-1 text-xs font-medium text-white rounded transition-colors ${style.buttonClass}`}
                    >
                      {meta.action_label || 'Edit agent'}
                    </button>
                  ) : meta.action_url && /^https?:\/\//i.test(meta.action_url) ? (
                    <a
                      href={meta.action_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`px-3 py-1 text-xs font-medium text-white rounded transition-colors ${style.buttonClass}`}
                    >
                      {meta.action_label || 'Open'}
                    </a>
                  ) : meta.action_url ? (
                    <a
                      href={meta.action_url}
                      className={`px-3 py-1 text-xs font-medium text-white rounded transition-colors ${style.buttonClass}`}
                    >
                      {meta.action_label || 'Open'}
                    </a>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {hasFriendlyCallout ? (
        <details className="text-xs">
          <summary className="cursor-pointer text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
            Technical details
          </summary>
          <div className="mt-2 bg-red-50 dark:bg-red-900/20 p-2 rounded border border-red-200 dark:border-red-800 text-sm font-mono whitespace-pre-wrap max-h-40 overflow-y-auto text-red-800 dark:text-red-300">
            {runResult.error_message}
          </div>
        </details>
      ) : (
        <div>
          <h4 className="text-xs font-medium text-red-700 dark:text-red-400 mb-1">
            Error:
          </h4>
          <div className="bg-red-50 dark:bg-red-900/20 p-2 rounded border border-red-200 dark:border-red-800 text-sm font-mono whitespace-pre-wrap max-h-40 overflow-y-auto text-red-800 dark:text-red-300">
            {runResult.error_message}
          </div>
        </div>
      )}

      {showUnderpoweredCallout && meta && (
        <div
          role="alert"
          aria-live="polite"
          className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg"
        >
          <div className="flex items-start space-x-2">
            <AlertTriangle
              aria-hidden="true"
              className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                This agent may be underpowered.
              </p>
              <p className="mt-1 text-xs text-yellow-700 dark:text-yellow-300">
                The model{' '}
                <code className="px-1 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/40">
                  {meta.model}
                </code>{' '}
                likely couldn&apos;t handle this agent&apos;s complexity within
                the turn/time budget. Try{' '}
                <code className="px-1 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/40">
                  {meta.suggested_model}
                </code>
                .
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => onEditAgent?.(meta.agent_id, 'model')}
                  className="px-3 py-1 text-xs font-medium bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
                >
                  Edit agent → upgrade model
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RunResultErrorPanel;
